<template>
  <div class="modal fade" ref="modal" data-bs-backdrop="static" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-0 shadow-lg" style="border-radius: 16px;">

        <div class="bg-danger modal-header pe-4 text-white" style="border-radius: 16px 16px 0 0;">
          <div>
            <h5 class="modal-title fw-bold mb-0">
              <i class="fa-solid fa-ban me-2" />
              {{ $t('Cancel Item') }}
            </h5>
            <div class="small opacity-75 mt-1">
              {{ item.product_name }}
              <span v-if="item.queue_no">&nbsp;|&nbsp;#{{ item.queue_no }}</span>
            </div>
          </div>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" />
        </div>

        <div class="modal-body px-4 py-4">

          <div v-if="loadingReasons" class="text-center py-4">
            <i class="fa-solid fa-spinner fa-spin fa-2x text-muted"></i>
          </div>

          <template v-else>
            <!-- Item Summary -->
            <div class="card bg-light border-0 rounded-3 mb-4">
              <div class="card-body p-3">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <div class="fw-bold">{{ item.product_name }}</div>
                    <div class="text-muted small">
                      x{{ remainingQty }}
                      <span v-if="alreadyCancelled > 0" class="text-muted ms-1" style="font-size: 0.8em;">
                        ({{ $t('of') }} {{ parseInt(item.quantity) }}, <s class="text-danger">{{ alreadyCancelled }}</s> {{ $t('cancelled') }})
                      </span>
                    </div>
                  </div>
                  <div class="fw-bold text-theme">{{ $storeIndex.formatThaiCurrency(item.total) }}</div>
                </div>
                <div v-if="item.modifiers && item.modifiers.length > 0" class="mt-2 ms-1">
                  <div
                    v-for="mod in item.modifiers"
                    :key="mod.id"
                    class="d-flex align-items-center gap-1 text-muted"
                    style="font-size: 0.78rem; line-height: 1.6;"
                  >
                    <i class="fa-solid fa-circle" style="font-size: 0.25rem;"></i>
                    <span>{{ mod.name }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Already-cancelled notice (only when partially cancelled before) -->
            <div v-if="alreadyCancelled > 0" class="alert alert-warning d-flex align-items-center py-2 px-3 mb-3" style="font-size: 0.85rem;">
              <i class="fa-solid fa-circle-info me-2"></i>
              <div>
                {{ $t('Previously cancelled') }}:
                <strong>{{ alreadyCancelled }}</strong>
                {{ $t('items') }} —
                {{ $t('Remaining') }}: <strong class="text-success">{{ remainingQty }}</strong>
              </div>
            </div>

            <!-- Cancel Quantity -->
            <div class="mb-4" v-if="remainingQty >= 1">
              <label class="form-label fw-semibold">
                {{ $t('Cancel Quantity') }} <span class="text-danger">*</span>
                <span class="text-muted fw-normal small ms-1">({{ $t('Max') }}: {{ remainingQty }})</span>
              </label>
              <div class="d-flex align-items-center gap-3">
                <button
                  class="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
                  style="width: 42px; height: 42px;"
                  type="button"
                  :disabled="form.cancel_quantity <= 1"
                  @click="form.cancel_quantity > 1 && form.cancel_quantity--"
                >
                  <i class="fa-solid fa-minus"></i>
                </button>
                <span class="fw-bold fs-3 px-3" style="min-width: 50px; text-align: center;">{{ form.cancel_quantity }}</span>
                <button
                  class="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
                  style="width: 42px; height: 42px;"
                  type="button"
                  :disabled="form.cancel_quantity >= remainingQty"
                  @click="form.cancel_quantity < remainingQty && form.cancel_quantity++"
                >
                  <i class="fa-solid fa-plus"></i>
                </button>
              </div>
            </div>

            <!-- Cancellation Reason -->
            <div class="mb-3">
              <label class="form-label fw-semibold">
                {{ $t('Cancellation Reason') }} <span class="text-danger">*</span>
              </label>
              <div class="d-flex flex-wrap gap-2">
                <button
                  v-for="reason in reasons"
                  :key="reason.id"
                  type="button"
                  class="btn rounded-pill px-3 py-2 fw-semibold"
                  :class="{
                    'btn-danger text-white': form.cancellation_reason_id === reason.id,
                    'btn-outline-secondary': form.cancellation_reason_id !== reason.id,
                  }"
                  @click="form.cancellation_reason_id = reason.id"
                >
                  {{ $t(reason.name) }}
                </button>
              </div>
              <div v-for="error of v$.form.cancellation_reason_id.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>

            <!-- Restock Toggle -->
            <div class="mb-3" v-if="remainingQty >= 1">
              <div class="form-check form-switch d-flex align-items-center gap-2 ps-0">
                <input
                  class="form-check-input ms-0"
                  type="checkbox"
                  role="switch"
                  id="restockSwitch"
                  v-model="form.is_restock"
                  style="width: 2.5rem; height: 1.3rem; cursor: pointer;"
                />
                <label class="form-check-label fw-semibold ms-2" for="restockSwitch" style="cursor: pointer;">
                  <i class="fa-solid fa-rotate-left me-1 text-success"></i>
                  {{ $t('Restock to inventory') }}
                </label>
              </div>

              <div v-if="form.is_restock" class="mt-3 ps-1">
                <label class="form-label fw-semibold small mb-2">
                  {{ $t('Restock Quantity') }} <span class="text-danger">*</span>
                  <span class="text-muted fw-normal ms-1">({{ $t('Max') }}: {{ form.cancel_quantity }})</span>
                </label>
                <div class="d-flex align-items-center gap-3">
                  <button
                    class="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
                    style="width: 38px; height: 38px;"
                    type="button"
                    :disabled="form.restock_quantity <= 1"
                    @click="form.restock_quantity > 1 && form.restock_quantity--"
                  >
                    <i class="fa-solid fa-minus"></i>
                  </button>
                  <span class="fw-bold fs-4 px-3" style="min-width: 50px; text-align: center;">{{ form.restock_quantity }}</span>
                  <button
                    class="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
                    style="width: 38px; height: 38px;"
                    type="button"
                    :disabled="form.restock_quantity >= form.cancel_quantity"
                    @click="form.restock_quantity < form.cancel_quantity && form.restock_quantity++"
                  >
                    <i class="fa-solid fa-plus"></i>
                  </button>
                </div>
                <div v-for="error of v$.form.restock_quantity.$errors"
                  class="d-block invalid-feedback"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </div>
              </div>
            </div>

            <!-- Refund Reason / Note -->
            <div class="mb-3">
              <label class="form-label fw-semibold">{{ $t('Note') }}</label>
              <textarea
                class="form-control shadow-none"
                v-model="form.cancelled_reason"
                rows="2"
                :placeholder="$t('Additional note')+'...'"
                style="font-size: 0.9rem; resize: none;"
              ></textarea>
            </div>
          </template>
        </div>

        <div class="modal-footer px-4 pb-4 pt-0 border-0">
          <button type="button" class="btn btn-sm btn-outline-secondary rounded-3 px-4" data-bs-dismiss="modal">
            {{ $t('Close') }}
          </button>
          <ladda
            :text="'Confirm Cancel'"
            :btn-class="'btn btn-danger rounded-3 px-4 fw-bold'"
            :loading="loadingSave"
            icon="<i class='fa-solid fa-ban'></i>"
            :disabled="v$.$invalid"
            @click="handleCancel"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import validationCancelItem from './assets/template/validation-cancel-item';
import { Sessions } from '@/services/endpoint/pos.service';
import { isServiceSessionInvalid } from '@/services/endpoint/services.service';
import Ladda from '@/plugins/Ladda.vue';

export default {
  name: 'ModalCancelItem',
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: {
    $lazy: true,
  },
  validations() {
    return validationCancelItem(this.form);
  },
  components: {
    Ladda
  },
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loadingReasons: true,
      loadingSave: false,
      reasons: [],
      form: {
        cancellation_reason_id: null,
        cancelled_reason: '',
        cancel_quantity: 1,
        is_restock: false,
        restock_quantity: 1,
      }
    };
  },
  computed: {
    token() {
      return this.$route.params.token;
    },
    alreadyCancelled() {
      return parseInt(this.item.cancelled_quantity) || 0;
    },
    remainingQty() {
      const qty = parseInt(this.item.quantity) || 0;
      return Math.max(0, qty - this.alreadyCancelled);
    },
  },
  watch: {
    'form.cancel_quantity'(val) {
      // Keep restock_quantity within [1, cancel_quantity]
      if (this.form.restock_quantity > val) {
        this.form.restock_quantity = val;
      }
      if (this.form.restock_quantity < 1) {
        this.form.restock_quantity = 1;
      }
    },
    'form.is_restock'(val) {
      if (val && (!this.form.restock_quantity || this.form.restock_quantity < 1)) {
        this.form.restock_quantity = this.form.cancel_quantity;
      }
    },
  },
  methods: {
    /**
     * 422 "service session invalid!" → the session this item belongs to is
     * gone. Close the modal and send staff back to the sessions list.
     * Returns true when handled so callers can stop.
     */
    bounceIfSessionInvalid(err) {
      if (!isServiceSessionInvalid(err)) return false;
      $(this.$refs.modal).modal('hide');
      this.$router.replace({ name: 'pos.sessions' });
      return true;
    },
    async fetchReasons() {
      this.loadingReasons = true;

      await Sessions.getCancelReasons(this.token, this.item.id).then(res => {
        this.reasons = res.data?.data?.cancellations ?? [];
      }).catch(err => {
        if (this.bounceIfSessionInvalid(err)) return;
        if (err?.response?.data?.message) {
          this.$toast.warn(this.$t(err?.response?.data?.message));
        }
      }).finally(() => {
        this.loadingReasons = false;
      });
    },
    async handleCancel() {
      this.v$.form.$touch();
      if (this.v$.form.$invalid) return;

      this.loadingSave = true;

      const payload = {
        cancellation_reason_id: this.form.cancellation_reason_id,
        cancelled_reason: this.form.cancelled_reason || null,
        cancel_quantity: this.form.cancel_quantity,
        is_restock: this.form.is_restock,
        restock_quantity: this.form.is_restock ? this.form.restock_quantity : null,
      };

      await Sessions.cancelItem(this.token, this.item.id, payload).then(() => {
        this.$toast.success(this.$t('Item cancelled successfully'));
        this.$emit('saved');
        $(this.$refs.modal).modal('hide');
      }).catch(err => {
        if (this.bounceIfSessionInvalid(err)) return;
        if (err?.response?.data?.message) {
          this.$toast.warn(this.$t(err?.response?.data?.message));
        }
      }).finally(() => {
        this.loadingSave = false;
      });
    }
  },
  mounted() {
    this.form.cancel_quantity = this.remainingQty || 1;
    this.form.restock_quantity = this.form.cancel_quantity;
    $(this.$refs.modal).modal('show');
    this.fetchReasons();
    $(this.$refs.modal).on('hidden.bs.modal', () => {
      this.$emit('close');
    });
  }
}
</script>
